import { useMemo } from "react";
import { aclRuleForPath, isAllowed, normalizeAclPath } from "@/lib/acl";
import { useCurrentUser } from "@/lib/auth";
import type { NavGroup, NavItem } from "@/lib/nav";

function navItemsKey(items: NavItem[]): string {
  return items.map((item) => item.href).join("|");
}

function filterNavByAccess(items: NavItem[], groups: string[]): NavItem[] {
  return items.filter((item) => {
    const path = normalizeAclPath(item.href);
    return isAllowed(groups, aclRuleForPath(path));
  });
}

export function useNavAccess(items: NavItem[]): NavItem[] {
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const itemsKey = navItemsKey(items);

  return useMemo(() => {
    if (!user) return [];
    return filterNavByAccess(items, groups);
    // itemsKey stabilizes deps when callers pass equivalent href sets with new array refs
    // eslint-disable-next-line react-hooks/exhaustive-deps -- items aligned with itemsKey
  }, [itemsKey, groups, user, items]);
}

export function useNavGroupAccess(groups: NavGroup[]): NavGroup[] {
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const allowedFlat = useNavAccess(flat);
  const allowedSet = useMemo(() => new Set(allowedFlat.map((i) => i.key)), [allowedFlat]);

  return useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => allowedSet.has(item.key)),
        }))
        .filter((group) => group.items.length > 0),
    [groups, allowedSet],
  );
}
